import React, { useState, useEffect } from "react";
import { Route, RouteComponentProps } from "react-router-dom";
import styled from "styled-components";
import { getEnvConfig } from "helpers/environment-config";
import {
  authorizedReportsBffRequest,
  getAuthorizedHtmlRequest,
} from "utils/api";
import { ReportView } from "model/Report";
import ReportNavigation from "./ReportNavigation";
import EmbeddedReport from "./EmbeddedReport";
import SelfServiceReportsList from "./selfServiceReporting/ReportsList";
import ManageReportReport from "./selfServiceReporting/ManageReport";
import SelfServiceViewReport from "./selfServiceReporting/ViewReport";
import {
  NEW_REPORTS,
  SELF_SERVICE_CREATE_REPORT,
  SELF_SERVICE_EDIT_REPORT,
  SELF_SERVICE_REPORTS_LIST,
  SELF_SERVICE_VIEW_REPORT,
} from "constants/routes";
import { MuiThemeProvider, createMuiTheme } from "@material-ui/core/styles";
import { useFlags } from "launchdarkly-react-client-sdk";
import { connect } from "react-redux";
import { compose } from "redux";

const PageLayout = styled.div`
  display: flex;
  min-height: 100%;
`;

interface NewReportsProps extends RouteComponentProps {
  companyId: number | null;
}

const NewReports = (props: NewReportsProps) => {
  const { history } = props;
  const [reportHTMLContent, setReportHTMLContent] = useState<string>("");
  const [availableReports, setAvailableReports] = useState<ReportView[]>([]);

  const {
    bar2007HideReportingMenu: tenantsWithHiddenReportsMenu,
    bar2164DisableDefaultReportId: tenantsWithDisabledDefaultReportId
  } = useFlags();
  const shouldShowReportsMenu = !(tenantsWithHiddenReportsMenu ?? []).includes(props.companyId);
  const shouldDisableDefaultReportId = (tenantsWithDisabledDefaultReportId ?? []).includes(props.companyId);

  const params = new URLSearchParams(history.location.search);
  const queriedReportId = params.get("reportId");

  useEffect(() => {
    const fetchData = async () => {
      const reportListPromise: Promise<ReportView[]> =
        authorizedReportsBffRequest("/standard-reports?includeHidden=false");

      const reportsOrderPromise: Promise<string[]> =
        authorizedReportsBffRequest("/standard-reports/order");

      const reportList = await reportListPromise;

      try {
        const reportsOrder = await reportsOrderPromise;
        const reversedReportsOrder = reportsOrder.slice().reverse();
        const getOrderIndex = (id: string) => reversedReportsOrder.indexOf(id);

        reportList.sort(
          (a, b) => getOrderIndex(b.reportId) - getOrderIndex(a.reportId)
        );

        if (history.location.pathname === "/reporting" && !queriedReportId) {
          let defaultReportId;

          if (reportsOrder.length > 0) {
            defaultReportId = reportList[0].reportId;
          } else {
            if (shouldDisableDefaultReportId) {
              defaultReportId = reportList.length > 0
                ? reportList[0].reportId
                : await getEnvConfig("reactAppReportsDebtSummary");
            } else {
              defaultReportId = await getEnvConfig("reactAppReportsDebtSummary");
            }
          }

          history.push(`/reporting?reportId=${defaultReportId}`);
        }
      } finally {
        setAvailableReports(reportList);

        if (queriedReportId) {
          const reportsBffUrl = await getEnvConfig("reactAppReportsApiUrl");

          const reportUrl = `${reportsBffUrl}/report-security/user-embed-token?reportId=${queriedReportId}`;

          const htmlData = await getAuthorizedHtmlRequest(reportUrl);
          setReportHTMLContent(htmlData);
        }
      }
    };

    fetchData();
  }, [queriedReportId, shouldDisableDefaultReportId]);

  const changeReportsOrder = (startIndex: number, endIndex: number) => {
    const reports = [...availableReports];
    const [reorderedItem] = reports.splice(startIndex, 1);
    reports.splice(endIndex, 0, reorderedItem);

    setAvailableReports(reports);

    authorizedReportsBffRequest(
      "/standard-reports/order",
      "POST",
      reports.map((r) => r.reportId)
    );
  };

  const theme = createMuiTheme({
    palette: {
      primary: {
        main: "#1468A5",
      },
    },
  });

  return (
    <MuiThemeProvider theme={theme}>
      <PageLayout>
        {shouldShowReportsMenu && (
          <ReportNavigation
            activeReportId={queriedReportId}
            availableReportList={availableReports}
            changeReportsOrder={changeReportsOrder}
          />
        )}
        <Route
          exact
          path={NEW_REPORTS}
          component={() => (
            <EmbeddedReport reportHTMLContent={reportHTMLContent} />
          )}
        />
        <Route
          exact
          path={SELF_SERVICE_REPORTS_LIST}
          component={SelfServiceReportsList}
        />
        <Route
          path={SELF_SERVICE_CREATE_REPORT}
          component={ManageReportReport}
        />
        <Route path={SELF_SERVICE_EDIT_REPORT} component={ManageReportReport} />
        <Route
          path={SELF_SERVICE_VIEW_REPORT}
          component={SelfServiceViewReport}
        />
      </PageLayout>
    </MuiThemeProvider>
  );
};

const mapStateToProps = ({ authentication }: any) => ({
  companyId: authentication?.user?.contact?.company?.id,
});

export default compose(
  connect(
    mapStateToProps
  )
)(NewReports);
